import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { FileText, Download, DollarSign, GraduationCap, MapPin } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OfferLetterModal = ({ open, onClose }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchOfferLetters();
    }
  }, [open]);

  const fetchOfferLetters = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/offer-letters`);
      setOffers(response.data);
    } catch (error) {
      console.error('Failed to fetch offer letters:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading font-semibold text-2xl">Offer Letters (University Admission)</DialogTitle>
          <p className="text-muted-foreground">View offer letters uploaded by your consultant</p>
        </DialogHeader>

        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : offers.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-lg">No offer letters yet</p>
              <p className="text-sm text-muted-foreground mt-2">Your consultant will upload offer letters once you receive university admissions</p>
            </div>
          ) : (
            <div className="space-y-4">
              {offers.map((offer, index) => (
                <Card key={offer.id} className="border-2 hover:border-primary/50 transition-all">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-success/10 flex items-center justify-center">
                          <GraduationCap className="h-6 w-6 text-success" />
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-xl text-foreground">{offer.university_name}</h3>
                          <p className="text-muted-foreground">{offer.course_title}</p>
                        </div>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                        offer.status === 'verified' ? 'bg-success/20 text-success' :
                        'bg-warning/20 text-warning'
                      }`}>
                        {offer.status === 'verified' ? 'VERIFIED' : 'PENDING VERIFICATION'}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="p-4 rounded-xl bg-muted">
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin className="h-4 w-4 text-primary" />
                          <span className="text-xs text-muted-foreground font-medium">Country</span>
                        </div>
                        <p className="font-semibold text-foreground">{offer.country}</p>
                      </div>

                      <div className="p-4 rounded-xl bg-muted">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="h-4 w-4 text-primary" />
                          <span className="text-xs text-muted-foreground font-medium">Tuition Fee</span>
                        </div>
                        <p className="font-semibold text-foreground">{offer.tuition_fee}</p>
                      </div>

                      <div className="p-4 rounded-xl bg-success/10">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="h-4 w-4 text-success" />
                          <span className="text-xs text-success-foreground font-medium">Scholarship</span>
                        </div>
                        <p className="font-semibold text-success">{offer.scholarship_amount}</p>
                      </div>
                    </div>

                    <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-primary" />
                          <div>
                            <p className="font-medium text-foreground">Offer Letter Document</p>
                            <p className="text-xs text-muted-foreground">Uploaded: {new Date(offer.uploaded_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <a
                          href={offer.offer_letter_url}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-4 py-2 rounded-full bg-primary text-white hover:bg-primary/90 transition-all flex items-center gap-2 text-sm font-medium"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="p-4 rounded-xl bg-info/10 border border-info/20 mt-6">
            <h4 className="font-semibold text-info-foreground mb-2">📋 Next Steps:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-info-foreground">
              <li>Review all offer letter details carefully</li>
              <li>Compare tuition fees and scholarship amounts</li>
              <li>Discuss with your consultant which offer to accept</li>
              <li>Download and save offer letters for your records</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OfferLetterModal;
